// Saving. The map is two 2 464-byte grids plus crops, animals and flags, which
// is comfortably more JSON than belongs in localStorage — so the real store is
// IndexedDB, with a localStorage mirror kept as a crash net.
//
// Writes are fire-and-forget: an IndexedDB transaction cannot be awaited on
// `beforeunload`, so every save also drops a copy into localStorage, and loading
// prefers whichever copy carries the newer timestamp.
//
// When the player is signed in there is a third tier, the server. It is a peer
// of the other two rather than a replacement: the local copy is still written
// first and synchronously, so the farm survives a flight-mode tunnel or a server
// hiccup, and every read picks whichever tier carries the newest `savedAt`.
// That is the same rule IndexedDB and the mirror already settle ties with.
import { SAVE_SLOTS } from "./config.ts";
import type { CloudSaves } from "./cloud.ts";

const DB_NAME = "farmstead";
const DB_VERSION = 1;
const STORE = "saves";
const MIRROR_PREFIX = "farmstead.save.v2.";
const SLOT_KEY = "farmstead.slot.v1";
/** how long to wait for IndexedDB before falling back to the mirror */
const OPEN_TIMEOUT = 1500;
/** the pre-slot save this build replaces */
export const LEGACY_KEY = "farmstead.save.v1";

export interface SlotMeta {
  slot: number;
  exists: boolean;
  day: number;
  gold: number;
  season: string;
  savedAt: number;
}

export interface SaveEnvelope {
  slot: number;
  savedAt: number;
  data: Record<string, unknown>;
}

export class SaveStore {
  private db: IDBDatabase | null = null;
  private opening: Promise<IDBDatabase | null> | null = null;
  /** the server tier, or null when signed out or sync is unavailable */
  private cloud: CloudSaves | null = null;
  /** the newest envelope written this session, per slot, for the unload beacon */
  private readonly latest = new Map<number, SaveEnvelope>();

  /** Attach the server tier once `/api/me` says the player is signed in. */
  useCloud(cloud: CloudSaves | null): void {
    this.cloud = cloud;
  }

  get syncing(): boolean {
    return this.cloud !== null;
  }

  private open(): Promise<IDBDatabase | null> {
    if (this.db) return Promise.resolve(this.db);
    if (this.opening) return this.opening;
    this.opening = new Promise((resolve) => {
      if (typeof indexedDB === "undefined") {
        resolve(null);
        return;
      }
      // A connection another tab still holds can leave `open` silently pending
      // forever. Nothing here is worth hanging the boot for: give up and let the
      // localStorage mirror answer instead.
      const giveUp = setTimeout(() => resolve(null), OPEN_TIMEOUT);
      const done = (db: IDBDatabase | null) => {
        clearTimeout(giveUp);
        resolve(db);
      };
      let req: IDBOpenDBRequest;
      try {
        req = indexedDB.open(DB_NAME, DB_VERSION);
      } catch {
        done(null);
        return;
      }
      req.onupgradeneeded = () => {
        const db = req.result;
        if (!db.objectStoreNames.contains(STORE)) db.createObjectStore(STORE, { keyPath: "slot" });
      };
      req.onsuccess = () => {
        this.db = req.result;
        // let a later version upgrade through rather than blocking it
        this.db.onversionchange = () => {
          this.db?.close();
          this.db = null;
          this.opening = null;
        };
        done(this.db);
      };
      req.onerror = () => done(null);
      req.onblocked = () => done(null);
    });
    return this.opening;
  }

  get activeSlot(): number {
    try {
      const v = Number(localStorage.getItem(SLOT_KEY));
      return Number.isInteger(v) && v >= 0 && v < SAVE_SLOTS ? v : 0;
    } catch {
      return 0;
    }
  }

  set activeSlot(slot: number) {
    try {
      localStorage.setItem(SLOT_KEY, String(slot));
    } catch {
      /* storage disabled */
    }
  }

  /** Fire-and-forget. Mirrors synchronously first so an unload cannot lose it. */
  write(slot: number, data: Record<string, unknown>): void {
    const env: SaveEnvelope = { slot, savedAt: Date.now(), data };
    this.latest.set(slot, env);
    try {
      localStorage.setItem(MIRROR_PREFIX + slot, JSON.stringify(env));
    } catch {
      /* over quota or disabled — IndexedDB is still the primary store */
    }
    void this.open().then((db) => {
      if (!db) return;
      try {
        const tx = db.transaction(STORE, "readwrite");
        tx.objectStore(STORE).put(env);
      } catch {
        /* the mirror already has it */
      }
    });
    // The server is the slowest tier and the only one that can be unreachable,
    // so it is pushed last and never waited on. A push that fails is kept and
    // retried on the next autosave.
    void this.cloud?.write(env);
  }

  /**
   * The unload path. `sendBeacon` is the only request that reliably outlives the
   * tab, and it cannot report back — so this is the one save that is sent blind.
   */
  flush(): void {
    if (!this.cloud) return;
    const env = this.latest.get(this.activeSlot);
    if (env) this.cloud.flush(env);
  }

  /** Push anything an earlier autosave failed to land. */
  retrySync(): void {
    void this.cloud?.retryDirty();
  }

  async read(slot: number): Promise<Record<string, unknown> | null> {
    const local = await this.readLocal(slot);
    const remote = this.cloud ? await this.cloud.read(slot) : null;
    // Newest wins, silently — and a tie goes to the server, so a fresh browser
    // picks up the farm rather than the empty slot it was born with.
    if (local && remote) return remote.savedAt >= local.savedAt ? remote.data : local.data;
    return (remote ?? local)?.data ?? null;
  }

  async remove(slot: number): Promise<void> {
    this.latest.delete(slot);
    try {
      localStorage.removeItem(MIRROR_PREFIX + slot);
    } catch {
      /* storage disabled */
    }
    await this.cloud?.remove(slot);
    const db = await this.open();
    if (!db) return;
    try {
      db.transaction(STORE, "readwrite").objectStore(STORE).delete(slot);
    } catch {
      /* nothing to delete */
    }
  }

  async list(): Promise<SlotMeta[]> {
    // One round trip for every slot, rather than one per slot.
    const remote = this.cloud ? await this.cloud.list() : [];
    const bySlot = new Map(remote.map((r) => [r.slot, r]));
    const out: SlotMeta[] = [];
    for (let slot = 0; slot < SAVE_SLOTS; slot++) {
      const local = await this.readLocal(slot);
      const cloud = bySlot.get(slot);
      // Show whichever copy the player would actually load.
      const useCloud = cloud && (!local || cloud.savedAt >= local.savedAt);
      if (useCloud) {
        out.push({ slot, exists: true, day: cloud.day, gold: cloud.gold, season: cloud.season, savedAt: cloud.savedAt });
      } else {
        out.push({
          slot,
          exists: local !== null,
          day: Number(local?.data.day ?? 0),
          gold: Number(local?.data.gold ?? 0),
          season: String(local?.data.season ?? ""),
          savedAt: Number(local?.savedAt ?? 0),
        });
      }
    }
    return out;
  }

  /** Whichever of the two browser-side copies is newer. */
  private async readLocal(slot: number): Promise<SaveEnvelope | null> {
    const mirror = this.readMirror(slot);
    const db = await this.open();
    const stored = db ? await this.readDb(db, slot) : null;
    if (stored && mirror) return stored.savedAt >= mirror.savedAt ? stored : mirror;
    return stored ?? mirror;
  }

  /** Pull a pre-slot save forward the first time this build runs. */
  migrateLegacy(): Record<string, unknown> | null {
    try {
      const raw = localStorage.getItem(LEGACY_KEY);
      if (!raw) return null;
      const parsed = JSON.parse(raw) as Record<string, unknown>;
      localStorage.removeItem(LEGACY_KEY);
      return parsed;
    } catch {
      return null;
    }
  }

  private readMirror(slot: number): SaveEnvelope | null {
    try {
      const raw = localStorage.getItem(MIRROR_PREFIX + slot);
      return raw ? (JSON.parse(raw) as SaveEnvelope) : null;
    } catch {
      return null;
    }
  }

  private readDb(db: IDBDatabase, slot: number): Promise<SaveEnvelope | null> {
    return new Promise((resolve) => {
      const giveUp = setTimeout(() => resolve(null), OPEN_TIMEOUT);
      const done = (v: SaveEnvelope | null) => {
        clearTimeout(giveUp);
        resolve(v);
      };
      try {
        const req = db.transaction(STORE, "readonly").objectStore(STORE).get(slot);
        req.onsuccess = () => done((req.result as SaveEnvelope) ?? null);
        req.onerror = () => done(null);
      } catch {
        done(null);
      }
    });
  }
}
